(() => {
  class CollectionProductSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.collection-product-search__results');
      this.status = this.querySelector('.collection-product-search__status');
      this.abortController = null;
      this.timer = null;
      this.activeIndex = -1;
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => this.queueSearch());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', () => window.setTimeout(() => this.closeIfFocusLeft(), 0));
    }

    queueSearch() {
      window.clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.close();
        return;
      }
      this.timer = window.setTimeout(() => this.search(query), 250);
    }

    async search(query) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setAttribute('loading', '');
      if (this.status) this.status.textContent = 'Searching products';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor'
      });

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Product search failed: ${response.status}`);
        const data = await response.json();
        this.render(data.resources?.results?.products || [], query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.close();
          if (this.status) this.status.textContent = 'Product search is temporarily unavailable';
        }
      } finally {
        this.removeAttribute('loading');
      }
    }

    render(products, query) {
      this.results.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'collection-product-search__empty';
        empty.textContent = `No products found for “${query}”`;
        this.results.appendChild(empty);
      } else {
        products.forEach((product, index) => {
          const link = document.createElement('a');
          link.className = 'collection-product-search__result';
          link.href = product.url;
          link.id = `CollectionProductSearchOption-${index}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');

          if (product.featured_image?.url) {
            const image = document.createElement('img');
            image.className = 'collection-product-search__result-image';
            const imageUrl = new URL(product.featured_image.url, window.location.origin);
            imageUrl.searchParams.set('width', '120');
            image.src = imageUrl.toString();
            image.alt = '';
            image.width = 56;
            image.height = 56;
            image.loading = 'lazy';
            link.appendChild(image);
          }

          const title = document.createElement('span');
          title.className = 'collection-product-search__result-title';
          title.textContent = product.title;
          link.appendChild(title);
          this.results.appendChild(link);
        });
      }

      this.results.hidden = false;
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.status) this.status.textContent = `${products.length} products found`;
    }

    onKeydown(event) {
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !this.results.hidden) {
        event.preventDefault();
        this.moveSelection(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if (event.key === 'Enter' && this.activeIndex >= 0) {
        const activeResult = this.results.querySelectorAll('.collection-product-search__result')[this.activeIndex];
        if (activeResult) {
          event.preventDefault();
          activeResult.click();
        }
        return;
      }
      if (event.key === 'Escape') {
        this.close();
        this.input.focus();
      }
    }

    moveSelection(direction) {
      const options = Array.from(this.results.querySelectorAll('.collection-product-search__result'));
      if (!options.length) return;
      options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.activeIndex = (this.activeIndex + direction + options.length) % options.length;
      const activeOption = options[this.activeIndex];
      activeOption.setAttribute('aria-selected', 'true');
      activeOption.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', activeOption.id);
    }

    closeIfFocusLeft() {
      if (!this.contains(document.activeElement)) this.close();
    }

    close() {
      this.results.hidden = true;
      this.results.replaceChildren();
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  if (!customElements.get('collection-product-search')) {
    customElements.define('collection-product-search', CollectionProductSearch);
  }

  const formatRangeValue = (range, value) => {
    const symbol = range.dataset.currencySymbol || '';
    return `${symbol}${Number(value).toLocaleString(document.documentElement.lang || 'en', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}`;
  };

  const updatePriceRange = (range, changedInput, submit = false) => {
    const minInput = range.querySelector('[data-range-min]');
    const maxInput = range.querySelector('[data-range-max]');
    const minField = range.querySelector('[data-range-min-field]');
    const maxField = range.querySelector('[data-range-max-field]');
    if (!minInput || !maxInput || !minField || !maxField) return;

    if (range.dataset.singlePrice === 'true') {
      const singleValue = Number(range.dataset.singleValue);
      range.style.setProperty('--range-start', '0%');
      range.style.setProperty('--range-end', '100%');
      range.querySelector('[data-range-min-output]').textContent = formatRangeValue(range, singleValue);
      range.querySelector('[data-range-max-output]').textContent = formatRangeValue(range, singleValue);
      minField.value = '';
      maxField.value = '';
      return;
    }

    let minValue = Number(minInput.value);
    let maxValue = Number(maxInput.value);
    if (minValue > maxValue) {
      if (changedInput === minInput) maxValue = minValue;
      else minValue = maxValue;
    }
    minInput.value = minValue;
    maxInput.value = maxValue;

    const absoluteMin = Number(range.dataset.min);
    const absoluteMax = Number(range.dataset.max);
    const span = absoluteMax - absoluteMin || 1;
    range.style.setProperty('--range-start', `${((minValue - absoluteMin) / span) * 100}%`);
    range.style.setProperty('--range-end', `${((maxValue - absoluteMin) / span) * 100}%`);
    range.querySelector('[data-range-min-output]').textContent = formatRangeValue(range, minValue);
    range.querySelector('[data-range-max-output]').textContent = formatRangeValue(range, maxValue);

    minField.value = minValue <= absoluteMin ? '' : minValue;
    maxField.value = maxValue >= absoluteMax ? '' : maxValue;
    if (submit) maxField.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const initializePriceRanges = (scope = document) => {
    const ranges = [];
    if (scope.matches?.('[data-collection-price-range]')) ranges.push(scope);
    scope.querySelectorAll?.('[data-collection-price-range]').forEach((range) => ranges.push(range));

    ranges.forEach((range) => {
      if (range.dataset.rangeInitialized === 'true') return;
      range.dataset.rangeInitialized = 'true';

      const minInput = range.querySelector('[data-range-min]');
      const maxInput = range.querySelector('[data-range-max]');

      [minInput, maxInput].forEach((input) => {
        input?.addEventListener('input', (event) => {
          // Keep dragging smooth; Dawn should not replace the facet DOM mid-drag.
          event.stopPropagation();
          updatePriceRange(range, event.currentTarget);
        });

        input?.addEventListener('change', (event) => {
          updatePriceRange(range, event.currentTarget, true);
        });
      });

      updatePriceRange(range);
    });
  };

  const observeFacetUpdates = () => {
    const filters = document.querySelector('#FacetFiltersForm');
    if (!filters || filters.dataset.priceRangeObserver === 'true') return;
    filters.dataset.priceRangeObserver = 'true';

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initializePriceRanges(node);
        });
      });
    }).observe(filters, { childList: true, subtree: true });
  };

  const initializeCollectionEnhancements = () => {
    initializePriceRanges();
    observeFacetUpdates();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCollectionEnhancements, { once: true });
  } else {
    initializeCollectionEnhancements();
  }
})();
